import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const },
};

const methods = [
  {
    id: "nail down",
    number: "01",
    title: "Traditional Nail Down",
    desc: "The standard for solid hardwood under 4 inches wide over a premium wood subfloor. We use precision pneumatic cleats to allow the floor to breathe and expand naturally.",
  },
  {
    id: "nail-glue",
    number: "02",
    title: "Nail with Glue Assist",
    desc: "Mandatory for planks 5 inches or wider. We use a combination of mechanical fasteners and a serpentine bead of elastomeric wood glue to ensure wide planks never shift, cup, or squeak.",
  },
  {
    id: "full-glue-nail",
    number: "03",
    title: "Full Glue and Nail",
    desc: "The ultimate lockdown method for very wide planks and custom floors. We full trowel the subfloor with adhesive and mechanically fasten the planks, creating an incredibly solid, acoustically deadened floor.",
  },
  {
    id: "glue down",
    number: "04",
    title: "Full Trowel Glue Down",
    desc: "The premium choice for engineered hardwood over concrete slabs. We use specialized moisture cured urethanes and silane adhesives that act as both a permanent bond and a supreme moisture vapor barrier.",
  },
  {
    id: "floating",
    number: "05",
    title: "Floating Systems (LVP & Laminate)",
    desc: "Floating floors rely on their locking systems and core strength. Many floating floors require transition moldings in doorways 3 feet or smaller to prevent buckling. To avoid chopping up your home's visual flow, we exclusively install premium engineered floating systems, like Quick Step Laminate and Cali Vinyl, capable of spanning massive distances without requiring unsightly doorway transitions.",
  },
];

const InstallationMethods = () => {
  return (
    <section className="bg-foreground py-16 md:py-24 lg:py-32">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-20">
          {/* Left  Sticky header */}
          <motion.div {...fadeUp} className="lg:sticky lg:top-32 lg:self-start">
            <p className="text-[10px] md:text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-4 flex items-center gap-2">
              <span className="w-7 h-px bg-primary inline-block" />
              Installation Science
            </p>
            <h2 className="font-display text-[clamp(26px,4vw,44px)] font-semibold text-primary-foreground leading-[1.12]">
              Prescribing the Right Fastening{" "}
              <em className="italic font-normal text-[hsl(var(--gold-300))]">
                Science.
              </em>
            </h2>
            <p className="mt-5 text-sm md:text-[15px] font-light text-muted-foreground/80 leading-relaxed">
              Every subfloor and wood profile requires a specific installation
              method to prevent squeaks, cupping, and failure. We utilize five
              distinct methods:
            </p>
          </motion.div>

          {/* Right  Accordion */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
          >
            <Accordion
              type="single"
              collapsible
              defaultValue="nail down"
              className="space-y-3"
            >
              {methods.map((method) => (
                <AccordionItem
                  key={method.id}
                  value={method.id}
                  className="border border-primary-foreground/10 rounded-2xl px-6 md:px-8 bg-primary-foreground/[.03] data-[state=open]:bg-primary-foreground/[.06] transition-colors duration-300"
                >
                  <AccordionTrigger className="py-5 md:py-6 hover:no-underline gap-4 [&>svg]:text-primary [&>svg]:w-5 [&>svg]:h-5">
                    <span className="flex items-center gap-4">
                      <span className="text-[11px] font-bold tracking-[.1em] text-primary">
                        {method.number}
                      </span>
                      <span className="font-display text-base md:text-lg font-semibold text-primary-foreground text-left leading-snug">
                        {method.title}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 md:pb-8 pt-0">
                    <p className="text-[13px] md:text-sm font-light text-primary-foreground/60 leading-relaxed">
                      {method.desc}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default InstallationMethods;
